// Design A ("Corporate Band") Purchase Return -- used ONLY for Print
// Preview (via PurchaseReturnPrint.jsx) when print_template is 'design_a'.
// Mirrors backend/src/pdfTemplates/designA/purchaseReturnAdapter.js exactly.
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { numberFormat, formatToCompanyTimezone } from "../../../../../lib/functions";
import { convertAmountToWordsDesignA } from "../../../../../lib/designAWords";
import { fitItemName } from "../../../../../lib/designAItemFit";
import { purchaseReturnWithDetailsApi } from "../../../../../apiservice/purchase";
import { getInstitutionApi } from "../../../../../apiservice/setting";
import { getPrimaryBankAccountApi, getBankAccountByIdsApi } from "../../../../../apiservice/accounts";
import { bankAccountToConfig } from "../../../../../lib/designABankAccount";
import { selectAuthInfo } from "../../../../Auth/authSelectors";
import VoucherBodyDesignA from "../../../../../components/common/VoucherBodyDesignA";

// Row budgets recalibrated for the taller "rich item" row (name line + an
// optional batch sub-line, see da-table--rich-item in VoucherBodyDesignA.css)
// -- mirrors purchaseReturnAdapter.js exactly.
const PURCHASE_RETURN_ROW_BUDGETS = { single: 10, first: 16, middle: 20, last: 9 };

// Same fixed-layout column widths as purchaseReturnAdapter.js -- see that
// file's comment for the reasoning. Keep both in sync.
const PAGE_CONTENT_WIDTH = 698;
const COL_WIDTH = { hash: 26, rate: 80, per: 40, purReturnQty: 95, discount: 68, tax: 46, total: 88, qty: 44, amount: 88 };
const CELL_PADDING = 16;
const CHAR_WIDTH_PX = 5.3;

function ItemCell({ name, maxChars, batchName }) {
  const fit = fitItemName(name, { maxChars });
  return (
    <div className="da-item-cell">
      <span className="da-item-name" style={{ fontSize: `${fit.fontSizePercent}%` }}>{fit.text}</span>
      {batchName && <div className="da-item-sub">{`Batch: ${batchName}`}</div>}
    </div>
  );
}

export default function PurchaseReturnVoucherBodyDesignA({ pur_r_id, showStamp = false, showSignature = false }) {
  const [record, setRecord] = useState(null);
  const [institution, setInstitution] = useState(null);
  const [primaryBankAccount, setPrimaryBankAccount] = useState(null);
  const [paymentBankAccount, setPaymentBankAccount] = useState(null);
  const { institutionData = {} } = useSelector((state) => state.lookups);
  const authInfo = useSelector(selectAuthInfo);

  useEffect(() => {
    if (!pur_r_id) return;
    purchaseReturnWithDetailsApi({ pur_r_id, from: "voucher" }).then((res) => {
      if (res?.data?.length) setRecord(res.data[0]);
    });
  }, [pur_r_id]);

  // This voucher's own payment (a refund coming back in) can be received
  // into a bank account that isn't marked primary -- prefer it when
  // present (see getBankAccountByIds's comment in
  // backend/src/controllers/accounts.js).
  useEffect(() => {
    if (!record) return;
    const toAccIds = (record.trans || []).map((t) => t.to_acc_id).filter(Boolean);
    if (!toAccIds.length) return;
    getBankAccountByIdsApi(toAccIds).then((res) => setPaymentBankAccount(res?.data?.data || null)).catch(() => {});
  }, [record]);

  useEffect(() => {
    getInstitutionApi().then((res) => setInstitution(res?.data || null)).catch(() => {});
    getPrimaryBankAccountApi().then((res) => setPrimaryBankAccount(res?.data?.data || null)).catch(() => {});
  }, []);

  if (!record) return null;

  const currency = institutionData?.currency || authInfo?.currency;
  const country = authInfo?.country || "";
  const isIndia = country.toLowerCase() === "india";
  const isIndiaGst = authInfo?.gst_enabled === "yes" && isIndia;
  const isGSTDisabled = isIndia && authInfo?.gst_enabled == "no";
  const taxLabel = isIndia ? "GST" : "VAT";
  const isUAE = country.trim().toLowerCase() === "united arab emirates";
  const taxRegLabel = isUAE ? "TRN No" : "Tax Reg No";

  const isIndividual = record.is_cal_type === "individual";
  const items = (record.details || []).map((item) => ({
    ...item,
    qty_display: `${item.pur_r_qty} ${item.unit_symbol}` + (item.conversion > 1 ? `, ${item.retail_qty} ${item.base_unit_name}` : ""),
  }));

  // No dedicated "Batch" column -- matches legacy (PurchaseReturnVoucherBody.jsx),
  // which shows batch_name as a sub-line under the item name, not a column.
  const usedWidth =
    COL_WIDTH.hash + COL_WIDTH.rate + COL_WIDTH.per +
    (isIndividual
      ? COL_WIDTH.purReturnQty + COL_WIDTH.discount + (!isGSTDisabled ? COL_WIDTH.tax : 0) + COL_WIDTH.total
      : COL_WIDTH.qty + COL_WIDTH.amount);
  const itemMaxChars = Math.max(20, Math.floor((PAGE_CONTENT_WIDTH - usedWidth - CELL_PADDING) / CHAR_WIDTH_PX));

  const columns = [{ label: "#", width: `${COL_WIDTH.hash}px` }, { label: "Item Name", cellClassName: "da-item-td" }];
  columns.push({ label: "Rate", align: "right", width: `${COL_WIDTH.rate}px` });
  columns.push({ label: "Per", align: "right", width: `${COL_WIDTH.per}px` });
  if (isIndividual) {
    columns.push({ label: "Pur Return QTY", align: "right", width: `${COL_WIDTH.purReturnQty}px` });
    columns.push({ label: "Discount %", align: "right", width: `${COL_WIDTH.discount}px` });
    if (!isGSTDisabled) columns.push({ label: taxLabel, align: "right", width: `${COL_WIDTH.tax}px` });
    columns.push({ label: "Total", align: "right", width: `${COL_WIDTH.total}px` });
  } else {
    columns.push({ label: "Quantity", align: "right", width: `${COL_WIDTH.qty}px` });
    columns.push({ label: "Amount", align: "right", width: `${COL_WIDTH.amount}px` });
  }

  const rows = items.map((d, i) => {
    const cells = [String(i + 1), <ItemCell key="n" name={d.item_name} maxChars={itemMaxChars} batchName={d.batch_name} />];
    cells.push(<span className="da-num-cell" key="r">{Number(isIndividual ? d.pur_r_rate : d.item_rate || 0).toFixed(2)}</span>);
    cells.push(isIndividual ? d.unit_name : d.per_unit_symbol);

    let rawAmount;
    if (isIndividual) {
      cells.push(<span className="da-num-cell" key="q">{d.pur_r_qty}</span>);
      cells.push(<span className="da-num-cell" key="d">{Number(d.item_discount_per || 0).toFixed(2).replace(/\.00$/, "")}</span>);
      if (!isGSTDisabled) cells.push(<span className="da-num-cell" key="t">{Number(d.item_tax_per || 0).toFixed(2).replace(/\.00$/, "")}</span>);
      let total = (d.pur_r_qty || 0) * (d.pur_r_rate || 0);
      total -= (Number(d.item_discount_per || 0) / 100) * total;
      total += (Number(d.item_tax_per || 0) / 100) * total;
      rawAmount = total;
    } else {
      cells.push(d.qty_display);
      rawAmount = Number(d.item_total || 0);
    }
    cells.push(<span className="da-amount-cell" key="a">{numberFormat(rawAmount)}</span>);

    return { cells, isBonus: false, rawAmount };
  });

  const totalTax = Number(record.total_tax || 0);
  const totalQty = items.reduce((sum, d) => sum + (Number(d.pur_r_qty) || 0), 0);

  const totalsRows = [];
  totalsRows.push({ label: "Sub Total", value: numberFormat(record.sub_total || 0) });
  totalsRows.push({ label: "Discount", value: `- ${numberFormat(record.total_discount || 0)}`, credit: true });

  let taxSummary = null;
  if (isIndiaGst) {
    if (record.place_of_supply_type === "intra") {
      totalsRows.push({ label: "CGST", value: numberFormat(record.cgst_amount) });
      totalsRows.push({ label: "SGST", value: numberFormat(record.sgst_amount) });
      taxSummary = { mode: "cgst_sgst", rows: [{ rate: `${record.item_tax_per || ""}%`, taxable: numberFormat((record.sub_total || 0) - (record.total_discount || 0)), cgst: numberFormat(record.cgst_amount), sgst: numberFormat(record.sgst_amount) }] };
    }
    if (record.place_of_supply_type === "inter") {
      totalsRows.push({ label: "IGST", value: numberFormat(record.igst_amount) });
      taxSummary = { mode: "igst", rows: [{ rate: `${record.item_tax_per || ""}%`, taxable: numberFormat((record.sub_total || 0) - (record.total_discount || 0)), igst: numberFormat(record.igst_amount) }] };
    }
    if (record.gst_calculated === "no") totalsRows.push({ label: taxLabel, value: numberFormat(totalTax) });
  } else if (!isIndia) {
    totalsRows.push({ label: taxLabel, value: numberFormat(totalTax) });
  }

  const grandTotal = isIndividual
    ? items.reduce((sum, d) => {
        const qty = d.pur_r_qty || 0;
        const rate = Number(d.pur_r_rate || 0);
        let total = qty * rate;
        total -= (Number(d.item_discount_per || 0) / 100) * total;
        total += (Number(d.item_tax_per || 0) / 100) * total;
        return sum + total;
      }, 0)
    : record.total_amount || 0;

  const bankDetails = bankAccountToConfig(paymentBankAccount || primaryBankAccount);

  const useBranch = !!authInfo?.branch_info;
  const companyName = (useBranch ? institution?.branch_name : institution?.name) || "Institution";

  const config = {
    docTitle: "PURCHASE RETURN",
    docNumber: record.pur_r_voucher_no,
    institution: { logoUrl: useBranch ? institution?.branch_logo : institution?.logo, companyName, companyGstin: isIndia ? institutionData?.vat_license : null },
    party: {
      billToLabel: "Supplier",
      billTo: {
        name: record.acc_name,
        lines: [
          record.acc_code ? `Supplier Code: ${record.acc_code}` : null,
          record.address ? `Address: ${record.address}` : null,
          record.contact_no ? `Contact No: +${record.contact_no}` : null,
          record.licence_no ? `License No: ${record.licence_no}` : null,
        ].filter(Boolean),
      },
      fromLabel: "From",
      from: { name: companyName, lines: [institutionData?.street_address || null, institutionData?.phone_no ? `+${institutionData.phone_no}` : null].filter(Boolean) },
      metaLabel: "Voucher Details",
      meta: [
        { label: "Expense Date", value: (formatToCompanyTimezone(record.created_date) || "").split(" ")[0] },
        { label: "Expense By", value: (record.full_name || "").split(" ")[0] },
        ...(institutionData?.vat_license ? [{ label: taxRegLabel, value: institutionData.vat_license }] : []),
      ],
    },
    columns,
    rows,
    amountFn: (slice) => numberFormat(slice.reduce((sum, r) => sum + (r.rawAmount || 0), 0)),
    totalItems: items.length,
    totalQty,
    totalsRows,
    grandTotalLabel: `Grand Total (${currency || ""})`,
    grandTotalValue: numberFormat(grandTotal),
    paidValue: numberFormat(record.paid_amount || 0),
    dueValue: numberFormat(record.due_total || 0),
    amountInWordsText: convertAmountToWordsDesignA(grandTotal, { country, currency: record?.currency || currency }),
    bankDetails,
    taxSummary,
    terms: institutionData?.invoice_default_terms || "-",
    signatory: {
      companyName,
      stampUrl: showStamp ? institutionData?.stamp : null,
      signatureUrl: showSignature ? institutionData?.signature : null,
    },
    bonusNote: null,
    rowBudgets: PURCHASE_RETURN_ROW_BUDGETS,
    tableClassName: "da-table--rich-item",
  };

  return <VoucherBodyDesignA config={config} />;
}
